import React     from 'react';
import PropTypes from 'prop-types';

class ErrorBoundary extends React.Component
{   static propTypes =
    {   /** The fallback UI to display when an error is caught */
        fallback : PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
        /** Function to call when an error is caught */
        onError  : PropTypes.func,
        /** The child components to render */
        children : PropTypes.node.isRequired,
    };

    static defaultProps =
    {   fallback: null,
        onError : null,
    };

    constructor(props)
    {   super(props);
        this.state =
        {   hasError : false,
            error    : null,
            errorInfo: null,
        };
    }

  /**
   * Update state so the next render will show the fallback UI.
   * @param {Error} error - The error that was thrown.
   * @returns {object} - The new state.
   */
    static getDerivedStateFromError(error)
    { return { hasError: true, error }; }

  /**
   * Called after an error has been thrown by a descendant component.
   * @param {Error}  error     - The error that was thrown.
   * @param {object} errorInfo - An object with information about the component stack.
   */
    componentDidCatch(error, errorInfo)
    {   // Call the onError prop function if provided
        if (typeof this.props.onError === 'function')
        {   try
            { this.props.onError.call(this, error, errorInfo); } 
            catch (e) { console.error('ErrorBoundary onError function threw an error:', e); }
        }
        else
        {   // Default error logging
            console.error('ErrorBoundary caught an error', error, errorInfo);
        }
        // Store errorInfo in state for detailed fallback UI
        this.setState({ errorInfo });
    }

    render()
    {   const { fallback }                   = this.props;
        const { hasError, error, errorInfo } = this.state;

        if (hasError)
        {   // If fallback is a function, call it with error details
            if (typeof fallback === 'function')
            {   return fallback(
                {   error,
                    errorInfo,
                    resetErrorBoundary: () => this.setState({ hasError: false }),
                });
            }

        // If fallback is a React node, render it directly
        if (fallback)
            return fallback;

        // Default fallback UI if none is provided
        return (
            <div style={styles.defaultFallback}>
                <h2>Something went wrong.</h2>
                {process.env.NODE_ENV === 'development' && (
                    <details style={{ whiteSpace: 'pre-wrap' }}>
                        {error && error.toString()}
                        <br />
                        {errorInfo && errorInfo.componentStack}
                    </details>
                )}
                <button
                    onClick={() => this.setState({ hasError: false })}
                    style={styles.retryButton}
                >
                Try Again
                </button>
            </div>
        );
    }

    // Render children if there's no error
    return this.props.children;
  }
}

const styles =
{   defaultFallback:
    {   padding         : '20px',
        textAlign       : 'center',
        backgroundColor : '#f2f2f2',
    },
    retryButton:
    {   marginTop       : '15px',
        padding         : '10px 20px',
        fontSize        : '16px',
        cursor          : 'pointer',
    },
};

export default ErrorBoundary;
